/**
 * Group Detail Page
 * 
 * Displays detailed information about a specific group
 * - Fetches group data by reference (UUID)
 * - Shows group information
 * - Displays status and metadata
 */

import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiCalendar,
  FiEdit2,
  FiRefreshCw,
  FiAlertCircle,
  FiLoader,
  FiCheckCircle,
  FiMinusCircle,
  FiHome,
  FiHash,
  FiFileText,
} from 'react-icons/fi';
import { HiUserGroup } from 'react-icons/hi';
import { useDetailPage } from '@/hooks';
import { groupService } from '@/services/groupService';
import Button from '@/components/Common/Button';
import Modal from '@/components/Common/Modal';
import GroupForm from '@/components/Forms/GroupForm';
import GroupAgentsSection from '@/components/GroupAgentsSection';
import GroupCertificatesSection from '@/components/GroupCertificatesSection';
import type { GroupFormData } from '@/components/Forms/GroupForm';
import styles from './GroupDetailPage.module.css';

/**
 * Format date to readable string
 */
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Get status label in Portuguese
 */
const getStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    active: 'Ativo',
    inactive: 'Inativo',
  };
  return statusLabels[status] || status;
};

/**
 * Get status icon
 */
const getStatusIcon = (status: string) => {
  const iconProps = { size: 16 };
  const statusIcons: Record<string, JSX.Element> = {
    active: <FiCheckCircle {...iconProps} />,
    inactive: <FiMinusCircle {...iconProps} />,
  };
  return statusIcons[status] || null;
};

const GroupDetailPage: FC = () => {
  const { reference } = useParams<{ reference: string }>();

  /**
   * Use detail page hook
   */
  const {
    data: group,
    isLoading,
    isError,
    error,
    refetch,
    isEditModalOpen,
    setIsEditModalOpen,
    handleBack,
    updateMutation,
  } = useDetailPage({
    reference,
    service: {
      getByReference: groupService.getGroupByReference,
      update: groupService.updateGroup,
    },
    queryKey: 'group',
    navigationPath: '/groups',
    successMessages: {
      update: { 
        title: 'Grupo atualizado!', 
        message: 'As alterações foram salvas com sucesso.' 
      },
    },
    errorMessages: {
      update: 'Erro ao atualizar grupo',
    },
  });

  /**
   * Loading State
   */
  if (isLoading) {
    return (
      <div className={styles.groupDetailPage}>
        <div className={styles.loadingContainer}>
          <FiLoader size={48} className={styles.spinner} />
          <p className={styles.loadingText}>Carregando dados do grupo...</p>
        </div>
      </div>
    );
  }

  /**
   * Error State
   */
  if (isError || !group) {
    return (
      <div className={styles.groupDetailPage}>
        <div className={styles.errorContainer}>
          <FiAlertCircle size={64} className={styles.errorIcon} />
          <h2 className={styles.errorTitle}>Erro ao carregar grupo</h2>
          <p className={styles.errorMessage}>
            {error instanceof Error ? error.message : 'Grupo não encontrado'}
          </p>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
            <Button variant="secondary" onClick={handleBack}>
              Voltar
            </Button>
            <Button variant="primary" onClick={() => refetch()}>
              Tentar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Handle form submit
   */
  const handleFormSubmit = async (data: GroupFormData) => {
    await updateMutation.mutateAsync(data);
  };

  /**
   * Loading State
   */
  if (isLoading) {
    return (
      <div className={styles.groupDetailPage}>
        <div className={styles.loadingContainer}>
          <FiLoader size={48} className={styles.spinner} />
          <p className={styles.loadingText}>Carregando dados do grupo...</p>
        </div>
      </div>
    );
  }

  /**
   * Error State
   */
  if (isError || !group) {
    return (
      <div className={styles.groupDetailPage}>
        <div className={styles.errorContainer}>
          <FiAlertCircle size={64} className={styles.errorIcon} />
          <h2 className={styles.errorTitle}>Erro ao carregar grupo</h2>
          <p className={styles.errorMessage}>
            {error instanceof Error ? error.message : 'Grupo não encontrado'}
          </p>
          <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
            <Button variant="secondary" onClick={handleBack}>
              Voltar
            </Button>
            <Button variant="primary" onClick={() => refetch()}>
              Tentar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.groupDetailPage}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={handleBack}
            aria-label="Voltar"
          >
            <FiArrowLeft size={20} />
          </button>
          <div className={styles.headerTitle}>
            <h1 className={styles.title}>Detalhes do Grupo</h1>
            <p className={styles.subtitle}>Informações completas do grupo</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            size="md"
            iconLeft={<FiRefreshCw size={16} />}
            onClick={() => refetch()}
          >
            Atualizar
          </Button>
          <Button
            variant="primary"
            size="md"
            iconLeft={<FiEdit2 size={16} />}
            onClick={() => setIsEditModalOpen(true)}
          >
            Editar
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Profile Card */}
        <div className={styles.profileCard}>
          <div className={styles.profileContent}>
            <div className={styles.groupIcon}>
              <HiUserGroup size={32} />
            </div>
            <div className={styles.profileInfo}>
              <h3 className={styles.groupName}>{group.name}</h3>
              <p className={styles.groupDescription}>
                {group.description || <span className={styles.noDescription}>Sem descrição</span>}
              </p>
              <div className={styles.groupMeta}>
                <div className={styles.metaItem}>
                  <span className={`${styles.statusBadge} ${styles[group.status]}`}>
                    {getStatusIcon(group.status)}
                    {getStatusLabel(group.status)}
                  </span>
                </div>
                <div className={styles.metaItem}>
                  <FiHome size={16} className={styles.metaIcon} />
                  Organização ID: {group.organizationId}
                </div>
                <div className={styles.metaItem}>
                  <FiCalendar size={16} className={styles.metaIcon} />
                  Criado em {formatDate(group.createdAt)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className={styles.detailsGrid}>
          {/* Group Information Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <FiFileText size={20} />
              </div>
              <h2 className={styles.cardTitle}>Informações Detalhadas</h2>
            </div>
            <div className={styles.infoList}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Nome do Grupo</span>
                <span className={styles.infoValue}>{group.name}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Descrição</span>
                <span className={styles.infoValue}>
                  {group.description || <span className={styles.noDescription}>Sem descrição</span>}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Status</span>
                <span className={`${styles.statusBadge} ${styles[group.status]}`}>
                  {getStatusIcon(group.status)}
                  {getStatusLabel(group.status)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Organização</span>
                <span className={styles.infoValue}>ID: {group.organizationId}</span>
              </div>
            </div>
          </div>

          {/* System Information Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <FiHash size={20} />
              </div>
              <h2 className={styles.cardTitle}>Informações do Sistema</h2>
            </div>
            <div className={styles.infoList}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>ID do Grupo</span>
                <span className={styles.infoValue}>{group.id}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Referência (UUID)</span>
                <span className={styles.infoValue}>{group.ref}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Criado em</span>
                <span className={styles.infoValue}>{formatDate(group.createdAt)}</span>
              </div>
              {group.updatedAt && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Última atualização</span>
                  <span className={styles.infoValue}>{formatDate(group.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Agents Section */}
        <div className={styles.sectionCard}>
          <GroupAgentsSection
            groupId={group.id}
            onUpdate={() => refetch()}
          />
        </div>

        {/* Certificates Section */}
        <div className={styles.sectionCard}>
          <GroupCertificatesSection
            groupId={group.id}
            onUpdate={() => refetch()}
          />
        </div>
      </div>

      {/* Edit Group Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Editar Grupo"
        description="Atualize as informações do grupo"
        size="md"
      >
        <GroupForm
          mode="edit"
          initialValues={group}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsEditModalOpen(false)}
          isLoading={updateMutation.isPending}
        />
      </Modal>
    </div>
  );
};

export default GroupDetailPage;
